// @flow
import { useMemo, useState, useEffect, type Node } from 'react';
import ThemeProvider from '@tablekit/theme/ThemeProvider';
import { AUTO_MODAL_SHOWN_KEY } from 'components/Modals/Provider';
import { type PageContextType } from 'components/PageContext';
import type { ParsedBlokType } from 'components/types';
import SeoContent from 'components/SeoContent';
import Layout from 'components/Layout';
import componentKeys from '../componentKeys';

type PropsType = {
  children: (PageContextType) => Node,
  pageContext: PageContextType,
  path: string
};

const SDK_SCRIPT_ID = 'storyblock-sdk-script';

const loadStoryblokBridge = (cb) => {
  if (document.querySelector(`script#${SDK_SCRIPT_ID}`)) {
    cb();
    return;
  }
  const script = document.createElement('script');
  script.id = SDK_SCRIPT_ID;
  script.type = 'text/javascript';
  script.src = `https://app.storyblok.com/f/storyblok-latest.js?t=${CONFIG.storyblok.accessToken}`;
  script.onload = cb;
  document.getElementsByTagName('head')[0].appendChild(script);
};

function parseStory(story) {
  const { body, post_body: postBody } = story.content || {};

  body
    .concat(postBody)
    .filter((blok) => blok !== undefined)
    .forEach((blok) => {
      if (blok.component === componentKeys.SHOP_LIST) {
        blok.shops.forEach((shop) => {
          shop.tags = (shop.tags || '')
            .split('\n')
            .map((tag) => tag.trim())
            .filter((tag) => !!tag);
        });
      }
    });
}

function loadStory(callback) {
  if (!window.storyblok) return;
  window.storyblok.get(
    {
      slug: window.location.pathname.slice(1),
      version: 'draft'
    },
    (data) => {
      parseStory(data.story);
      callback(data.story);
    }
  );
}

function checkAppLoaded(pageContext) {
  if (window.isAppReady) return;
  window.tsWebsiteLocaleDetection(pageContext);
}

export default function StoryblokEntry({
  pageContext,
  path,
  children
}: PropsType) {
  const { story: contextStory = {} } = pageContext || {};
  let [locale, country] = path.split('/')[1].split('-');
  if (!locale && pageContext.country) {
    locale = pageContext.country.code;
    country = pageContext.country.code;
  }
  const propStoryContent: ParsedBlokType = useMemo(
    () => ({
      ...contextStory,
      content: contextStory.content || {}
    }),
    [contextStory]
  );
  const [story, updateStory] = useState(propStoryContent);

  if (CONFIG.storyblok.version === 'draft') {
    const update = () => {
      loadStory((newStory) => {
        updateStory(newStory);
      });
    };

    useEffect(() => {
      // the following is a check for dev mode, in the build this is injected via SSR
      if (!window.tsWebsiteLocaleDetection) {
        import('../utils/localeDetection').then(() => {
          checkAppLoaded(pageContext);
        });
      } else {
        checkAppLoaded(pageContext);
      }
      // end check
      loadStoryblokBridge(() => {
        const { storyblok } = window;
        if (!storyblok || !storyblok.pingEditor) return;
        storyblok.pingEditor(() => {
          if (storyblok.inEditor) {
            try {
              localStorage.setItem(AUTO_MODAL_SHOWN_KEY, 'true');
            } catch (e) {
              console.error(e);
            }
            update();
            storyblok.enterEditmode();

            storyblok.on(['change', 'published'], () => {
              update();
            });

            storyblok.on('input', (payload) => {
              if (story && story.uuid === payload.story.uuid) {
                const { story: payloadStory = {} } = payload;
                payloadStory.content = storyblok.addComments(
                  payloadStory.content,
                  payloadStory.uuid
                );
                parseStory(payload.story);
                updateStory(payload.story);
              }
            });
          }
        });
      });
    }, []);
  }

  return (
    <ThemeProvider locale={locale} country={country}>
      <Layout pageContext={pageContext} path={path}>
        <SeoContent pageContext={pageContext} />
        {story && children(story)}
      </Layout>
    </ThemeProvider>
  );
}
